/// <reference types="vite/client" />
const API_BASE = import.meta.env.VITE_API_URL || '';

export const api = {
  getAdminHeaders() {
    const token = sessionStorage.getItem('adminToken');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  },

  async login(email: string) {
    const utmData = {
      utmSource: sessionStorage.getItem('utm_source'),
      utmMedium: sessionStorage.getItem('utm_medium'),
      utmCampaign: sessionStorage.getItem('utm_campaign'),
      utmTerm: sessionStorage.getItem('utm_term'),
      utmContent: sessionStorage.getItem('utm_content'),
    };

    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, ...utmData })
    });
    if (!res.ok) throw new Error('Failed to login');
    return res.json();
  },

  async getUsers() {
    const res = await fetch(`${API_BASE}/api/users`, {
      headers: this.getAdminHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch users');
    return res.json();
  },

  async createUser(user: any) {
    const res = await fetch(`${API_BASE}/api/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(user)
    });
    if (!res.ok) throw new Error('Failed to create user');
    return res.json();
  },

  async updateUser(id: string, user: any) {
    const res = await fetch(`${API_BASE}/api/users/${id}`, {
      method: 'PUT',
      headers: this.getAdminHeaders(),
      body: JSON.stringify(user)
    });
    if (!res.ok) throw new Error('Failed to update user');
    return res.json();
  },

  async deleteUser(id: string) {
    const res = await fetch(`${API_BASE}/api/users/${id}`, {
      method: 'DELETE',
      headers: this.getAdminHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete user');
  },

  async getMixes(userId: string) {
    const res = await fetch(`${API_BASE}/api/users/${userId}/mixes`);
    if (!res.ok) throw new Error('Failed to fetch mixes');
    return res.json();
  },

  async createMix(userId: string, mix: any) {
    const res = await fetch(`${API_BASE}/api/users/${userId}/mixes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(mix)
    });
    if (!res.ok) throw new Error('Failed to create mix');
    return res.json();
  },

  async updateMix(id: string, mix: any) {
    const res = await fetch(`${API_BASE}/api/mixes/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(mix)
    });
    if (!res.ok) throw new Error('Failed to update mix');
    return res.json();
  },

  async deleteMix(id: string) {
    const res = await fetch(`${API_BASE}/api/mixes/${id}`, {
      method: 'DELETE'
    });
    if (!res.ok) throw new Error('Failed to delete mix');
  },

  async getSettings() {
    try {
      const res = await fetch(`${API_BASE}/api/settings`);
      if (!res.ok) {
        return { GA_ID: import.meta.env.VITE_GOOGLE_ANALYTICS_ID || '', META_PIXEL_ID: '', GSC_VERIFICATION: '' };
      }
      return res.json();
    } catch {
      return { GA_ID: import.meta.env.VITE_GOOGLE_ANALYTICS_ID || '', META_PIXEL_ID: '', GSC_VERIFICATION: '' };
    }
  },

  async getAdminSettings() {
    const res = await fetch(`${API_BASE}/api/admin/settings`, {
      headers: this.getAdminHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch admin settings');
    return res.json();
  },

  async getAdminStats() {
    const res = await fetch(`${API_BASE}/api/admin/stats`, {
      headers: this.getAdminHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch admin statistics');
    return res.json();
  },

  async updateSettings(settings: Record<string, string>) {
    const res = await fetch(`${API_BASE}/api/admin/settings`, {
      method: 'POST',
      headers: this.getAdminHeaders(),
      body: JSON.stringify(settings)
    });
    if (!res.ok) throw new Error('Failed to update settings');
    return res.json();
  },

  async createCheckoutSession(priceId: string, userId: string, mode: 'subscription' | 'payment' = 'subscription') {
    const res = await fetch(`${API_BASE}/api/create-checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ priceId, userId, mode })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to create checkout session');
    }
    return res.json();
  },

  async createCorporateCheckoutSession(data: { companyName: string, domain: string, seatCount: number, email: string }) {
    const res = await fetch(`${API_BASE}/api/corporate/create-checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to create corporate checkout session');
    }
    return res.json();
  },

  async getCorporateStats(companyId: string) {
    const res = await fetch(`${API_BASE}/api/corporate/${companyId}/stats`);
    if (!res.ok) throw new Error('Failed to fetch corporate stats');
    return res.json();
  },

  async searchKB(query: string) {
    const res = await fetch(`${API_BASE}/api/search-kb`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to query AI search');
    }
    return res.json();
  }
};
